// KBE-741: canonical list of KBE feature flags. This is the source of truth
// for which flags exist — a flag is only real if it has an entry here. DB rows
// (feature_flag / feature_flag_user) only ever turn a declared flag ON; a flag
// with no matching row is always OFF. Flags are TEMPORARY: add one to
// gate an in-progress feature, then delete the entry (and its dead code) once
// the feature ships. See docs/feature-flags.md.

export type FeatureFlagDefinition = {
  key: string
  label: string
  description: string
}

// Referenced by the server-side editing guard (assertNetworkWritable). Kept
// as a named constant so the guard and the registry entry can't drift apart.
export const DISABLE_MAIN_AND_RELEASE_BRANCH_EDITING_FLAG_KEY =
  'disable_main_and_release_branch_editing'

export const COLLABORATION_PAGES_FLAG_KEY = 'collaboration_pages'

// Global kill-switch for the nightly node-definition consistency review cron
// (KBE-898). Default off so the feature can ship dark and be validated via the
// `review_node_consistency` Marvin tool first; turn on to start the automated
// scan + researcher email. Read server-side by the cron via
// `isFlagGloballyEnabled` (a cron has no user, so the per-user allowlist is not
// consulted).
export const NIGHTLY_CONSISTENCY_REVIEW_FLAG_KEY = 'nightly_consistency_review'

export const FEATURE_FLAG_DEFINITIONS = [
  {
    key: DISABLE_MAIN_AND_RELEASE_BRANCH_EDITING_FLAG_KEY,
    label: 'Disable main and release branch editing',
    description:
      'Global kill-switch (default off = editing allowed, current behavior). Turn on to lock the master network and release branches against direct edits — changes must then be made in a working copy and merged in. Merging into them still works. Per-user allowlist is not consulted for this flag.',
  },
  {
    key: COLLABORATION_PAGES_FLAG_KEY,
    label: 'Collaboration pages',
    description:
      'Shows the Collaboration sidebar section with links to Discussions and Issues. Enable per researcher while these pages are being rolled out.',
  },
  {
    key: NIGHTLY_CONSISTENCY_REVIEW_FLAG_KEY,
    label: 'Nightly node consistency review',
    description:
      'Global switch (default off) for the nightly cron that reviews master-network node definitions against their tags and emails conflicts to the medical researchers. Off = the cron no-ops; the review_node_consistency Marvin tool works regardless. Per-user allowlist is not consulted for this flag.',
  },
] as const satisfies readonly FeatureFlagDefinition[]

export type FeatureFlagKey = (typeof FEATURE_FLAG_DEFINITIONS)[number]['key']

export const FEATURE_FLAG_KEYS: readonly FeatureFlagKey[] =
  FEATURE_FLAG_DEFINITIONS.map((definition) => definition.key)

const FEATURE_FLAG_KEY_SET: ReadonlySet<string> = new Set(FEATURE_FLAG_KEYS)

export function isFeatureFlagKey(value: string): value is FeatureFlagKey {
  return FEATURE_FLAG_KEY_SET.has(value)
}

export function allFlagsOff(): Record<FeatureFlagKey, boolean> {
  return Object.fromEntries(
    FEATURE_FLAG_KEYS.map((key) => [key, false]),
  ) as Record<FeatureFlagKey, boolean>
}
